import React from 'react';

const toNumber = (raw, fallback = 0) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  if (raw === null || raw === undefined) return fallback;
  const cleaned = `${raw}`.replace(/[$,%\s]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
};

export default function LiveTradingPanel({
  backendUrl,
  liveToken,
  C,
  cardStyle,
  showShare = false,
  compact = false,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [lastUpdated, setLastUpdated] = React.useState('');
  const [payload, setPayload] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [tunnelUrl, setTunnelUrl] = React.useState(() => localStorage.getItem('ibkr_tunnel_url') || '');
  const [showSetup, setShowSetup] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState('');
  const [paperMode, setPaperMode] = React.useState(() => localStorage.getItem('ibkr_paper') === 'true');

  const fetchLive = React.useCallback(async () => {
    if (!tunnelUrl || !liveToken) {
      setError(!tunnelUrl ? 'Paste your tunnel URL in Setup first.' : 'Missing live token.');
      return;
    }
    setLoading(true);
    try {
      const base = tunnelUrl.replace(/\/$/, '');
      const params = new URLSearchParams({ token: liveToken });
      if (paperMode) params.set('ibkr_base', 'https://localhost:5002/v1/api');
      const res = await fetch(`${base}/api/ibkr/live?${params}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || json?.detail || 'Live feed request failed.');
      setPayload(json);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('');
    } catch (err) {
      setError(err?.message || 'Connection failed.');
    } finally {
      setLoading(false);
    }
  }, [tunnelUrl, liveToken, paperMode]);

  React.useEffect(() => {
    if (!tunnelUrl) return;
    fetchLive();
    const id = setInterval(fetchLive, 10000);
    return () => clearInterval(id);
  }, [fetchLive, tunnelUrl]);

  const saveTunnelUrl = () => {
    localStorage.setItem('ibkr_tunnel_url', tunnelUrl);
    localStorage.setItem('ibkr_paper', String(paperMode));
    setSaveStatus('Saved!');
    setTimeout(() => setSaveStatus(''), 2000);
    setShowSetup(false);
    setPayload(null);
    setError('');
  };

  const pnlRows = Array.isArray(payload?.pnl) ? payload.pnl : (payload?.pnl ? [payload.pnl] : []);
  const netPnl = pnlRows.reduce((sum, row) => {
    const daily = toNumber(row?.dpl ?? row?.daily ?? row?.dailyPnL ?? row?.dailyPnl, 0);
    const unrealized = toNumber(row?.upl ?? row?.unrealized ?? row?.unrealizedPnL ?? row?.uplPnL, 0);
    const realized = toNumber(row?.rpl ?? row?.realized ?? row?.realizedPnL ?? row?.rplPnL, 0);
    return sum + daily + unrealized + realized;
  }, 0);
  const positions = Array.isArray(payload?.positions) ? payload.positions : [];
  const recentTrades = Array.isArray(payload?.recentTrades) ? payload.recentTrades : [];
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/live/${encodeURIComponent(liveToken)}` : '';

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) {}
  };

  const isIbkrError = error && (error.includes('IBKR') || error.includes('connect') || error.includes('timed out') || error.includes('upstream') || error.includes('7496'));

  return (
    <div style={{ fontFamily: C.font }}>

      {/* Header card */}
      <div style={{ ...cardStyle, padding: compact ? '14px' : '20px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ color: C.text, fontSize: compact ? 14 : 17, fontWeight: 800 }}>Live IBKR Session</div>
            <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>
              {lastUpdated ? `Last updated ${lastUpdated} · auto-refresh 10s` : 'Connect your IBKR TWS/Gateway to see live data'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Paper / Live toggle */}
            <div style={{ display: 'flex', borderRadius: 7, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              {[['Paper', true], ['Live', false]].map(([label, isPaper]) => (
                <button key={label} onClick={() => { setPaperMode(isPaper); localStorage.setItem('ibkr_paper', String(isPaper)); }}
                  style={{ padding: '6px 12px', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    background: paperMode === isPaper ? (isPaper ? `${C.blue}33` : `${C.green}22`) : 'transparent',
                    color: paperMode === isPaper ? (isPaper ? C.blueBright : C.greenBright) : C.sub }}>
                  {label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowSetup(s => !s)} style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.blue}66`, background: showSetup ? `${C.blue}22` : 'transparent', color: C.blueBright, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
              ⚙ Setup
            </button>
            {tunnelUrl && (
              <button onClick={fetchLive} disabled={loading} style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {loading ? '...' : 'Refresh'}
              </button>
            )}
            {showShare && (
              <button onClick={copyLink} style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.blue}66`, background: 'rgba(56,139,253,0.14)', color: C.blueBright, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {copied ? 'Copied!' : 'Copy Live Link'}
              </button>
            )}
          </div>
        </div>

        {/* Setup panel */}
        {showSetup && (
          <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>IBKR Connection Setup</div>

            <div style={{ color: C.sub, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
              The tunnel exposes your <strong style={{ color: C.text }}>local backend</strong> to the internet so it can reach IBKR TWS on your Mac.
              Paper trading uses port <strong style={{ color: C.blueBright }}>7497</strong>, live uses <strong style={{ color: C.greenBright }}>7496</strong>.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: '6px 10px', fontSize: 12, color: C.sub, marginBottom: 14 }}>
              {[
                ['1.', `Open IBKR TWS, log into your ${paperMode ? 'Paper Trading' : 'Live'} account`],
                ['2.', 'Enable API: TWS → Edit → Global Configuration → API → Settings → check "Enable ActiveX and Socket Clients"'],
                ['3.', 'In Terminal: bash ~/Desktop/trading-journal/scripts/install_live_services.sh'],
                ['4.', 'Get tunnel URL: bash ~/Desktop/trading-journal/scripts/live_tunnel_url.sh'],
                ['5.', 'Paste the .trycloudflare.com URL below (no /v1/api needed)'],
              ].map(([n, t]) => (
                <React.Fragment key={n}>
                  <span style={{ color: C.blue, fontWeight: 800 }}>{n}</span>
                  <span>{t}</span>
                </React.Fragment>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={tunnelUrl}
                onChange={e => setTunnelUrl(e.target.value)}
                placeholder="https://xxxx.trycloudflare.com"
                style={{ flex: 1, background: C.input || C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 12px', color: C.text, fontSize: 12, outline: 'none' }}
              />
              <button onClick={saveTunnelUrl} style={{ padding: '8px 16px', background: C.blue, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                {saveStatus || 'Save & Connect'}
              </button>
            </div>
            <div style={{ color: C.sub, fontSize: 10, marginTop: 6 }}>
              Saved in your browser. Tunnel URL changes on each restart — re-run live_tunnel_url.sh to refresh it.
            </div>
          </div>
        )}

        {/* Not configured yet */}
        {!tunnelUrl && !showSetup && (
          <div style={{ background: `${C.blue}10`, border: `1px solid ${C.blue}33`, borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: C.sub, fontSize: 12 }}>
              <span style={{ color: C.blueBright, fontWeight: 700 }}>Setup required:</span> Click ⚙ Setup to connect your IBKR TWS or Gateway
            </div>
            <button onClick={() => setShowSetup(true)} style={{ padding: '6px 14px', background: C.blue, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
              Get Started →
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}44`, borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
            <div style={{ color: C.redBright, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Connection Error</div>
            <div style={{ color: C.redBright, fontSize: 11 }}>{error}</div>
            {isIbkrError && (
              <div style={{ color: C.sub, fontSize: 11, marginTop: 6 }}>
                Make sure IBKR TWS is running, the API is enabled on port 7496, and ngrok is forwarding traffic. Click ⚙ Setup for instructions.
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ color: C.sub, fontSize: 12, padding: '8px 0' }}>Connecting to IBKR...</div>
        )}

        {/* Live stats */}
        {!loading && payload && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginTop: 4 }}>
            {[
              ['Accounts', `${Array.isArray(payload?.accounts) ? payload.accounts.length : 0}`],
              ['Open Positions', `${positions.length}`],
              ['Recent Trades', `${recentTrades.length}`],
              ['Live P&L', `${netPnl >= 0 ? '+' : ''}$${netPnl.toFixed(2)}`],
            ].map(([label, value]) => (
              <div key={label} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ color: C.sub, fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>{label}</div>
                <div style={{ color: label === 'Live P&L' ? (netPnl >= 0 ? C.greenBright : C.redBright) : C.text, fontSize: 16, fontWeight: 800 }}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Positions */}
      {!loading && payload && positions.length > 0 && (
        <div style={{ ...cardStyle, padding: compact ? 12 : 16 }}>
          <div style={{ color: C.sub, fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>Open Positions</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {positions.slice(0, compact ? 8 : 20).map((p, i) => {
              const pos = toNumber(p.position ?? p.qty ?? p.size, 0);
              const upl = toNumber(p.unrealizedPnl ?? p.upl ?? p.unrealized, 0);
              return (
                <div key={`${p.conid || p.symbol || i}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.6fr 0.8fr', gap: 8, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.card2, fontSize: 12 }}>
                  <div style={{ color: C.text, fontWeight: 700 }}>{p.symbol || p.contractDesc || 'Instrument'}</div>
                  <div style={{ color: C.sub }}>{pos > 0 ? `+${pos}` : pos}</div>
                  <div style={{ color: upl >= 0 ? C.greenBright : C.redBright, fontWeight: 700 }}>{upl >= 0 ? '+' : ''}${upl.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
